import React, { memo, useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// layout components
import ErrorBoundary from "engine/ErrorBoundary";

// antd component
import { Pagination } from "antd";

// Communication components
import { MessagesList } from "components/Communications";
// inner components
import { CreateMessageMobile } from "./components";

// utils
import api from "utils/api";
import { getObjId } from "utils/helpers/helper.general";

// hooks
import { useAsync, useRefState } from "utils/hooks";
import _local from "utils/localStorageUtils";

const DiscussionMobile = ({
  discussionId,
  communicationId,
  parentCommunicationID,
  className,
}) => {
  const childClassNames = classNames("discussion-app", className);
  const { t } = useTranslation();

  const newKey = "new";

  const localUser = _local.getLocalUser();

  const {
    data: dataList,
    isLoading: isLoadingList,
    isSuccess: isSuccessList,
    run: getList,
  } = useAsync();

  const [firstLoad, setFirstLoad] = useState(false);
  const [messagesArray, messagesArrayRef, setMessagesArray] = useRefState([]);
  const [sendMessageLoading, setSendMessageLoading] = useState(false);
  const [reply, setReply] = useState(null);
  const [currentPage, setCurrentPage] = useState(dataList?.page || 1);
  const [currentMessage, setCurrentMessage] = useState(false);
  const [scrollToMsg, setScrollToMsg] = useState(false);

  const messagesListRef = useRef(null);

  useEffect(() => {
    return () => {
      setMessagesArray([]);
      setCurrentMessage(false);
      setCurrentPage(1);
    };
  }, []);

  // Because history not trigger update component
  useEffect(() => {
    if (!!communicationId && communicationId !== newKey && !!discussionId) {
      // Loading data for rendering in a component
      getMessages({ commId: communicationId });
      setFirstLoad(true);
      setScrollToMsg(false);
    }
  }, [discussionId, communicationId]);

  // Set lodaded data for rendering in a component
  useEffect(() => {
    if (!isLoadingList && !!dataList) {
      setMessagesArray(dataList?.docs || []);
      setCurrentMessage(false);
      if (dataList?.page != currentPage) {
        setCurrentPage(dataList?.page);
      }
      // mark discussion as read when component mounted
      if (!!firstLoad && !!discussionId) {
        markAsReadHandle();
      }
      setFirstLoad(false);
    }
  }, [dataList]);

  // mark discussion as read (only for all admins)
  const markAsReadHandle = useCallback(async () => {
    !!discussionId &&
      (await api.discussions.markAsRead(getObjId(discussionId)));
  }, [discussionId]);

  const getMessages = useCallback(
    ({ page = 1, scrollToMsg = null, commId = null }) => {
      setCurrentMessage(false);
      const ID = commId ? commId : communicationId;

      if (!!ID) {
        const requestData = {
          communicationId: ID,
          page,
          sortBy: -1,
          scrollToMsg,
        };
        getList(api.discussions.getDiscussionMessagesById(requestData));
      } else {
        return null;
      }
    },
    [communicationId, discussionId],
  );

  const onSendMessage = savedData => {
    if (!!savedData) {
      setCurrentMessage(false);
      setSendMessageLoading(true);

      api.discussions
        .sendMessage({
          communication: communicationId,
          discussion: discussionId,
          type: reply ? "replay" : "default",
          replayOn: reply?._id,
          parentCommunication:
            (!!parentCommunicationID && parentCommunicationID) || null,
          ...savedData,
        })
        .then(resultMessage => {
          setCurrentMessage(resultMessage);
          setSendMessageLoading(false);
          setScrollToMsg(false);
          setReply(null);
          let msgs = messagesArrayRef?.current;
          const hasNewMsg = msgs?.find(msg => msg?._id == resultMessage?._id);
          if (!hasNewMsg) {
            msgs.push(resultMessage);
            setMessagesArray(msgs);
          }

          if (currentPage != 1) {
            getMessages({ page: 1 });
          }
        })
        .catch(e => {
          setCurrentMessage(false);
          setSendMessageLoading(false);
          console.error(e);
          setReply(null);
          setScrollToMsg(false);
        });
    }
  };

  const handleMessagesPagination = page => {
    setCurrentPage(page);
    getMessages({ page });
  };

  const onRelyHandle = useCallback(
    messageId => {
      if (!!messageId) {
        setCurrentMessage(false);
        setScrollToMsg(messageId);
        getMessages({ page: currentPage, scrollToMsg: messageId });
      }
    },
    [currentPage],
  );

  return (
    !!discussionId && (
      <div className={childClassNames}>
        <ErrorBoundary>
          {!!dataList && (
            <div className="messages--pagination">
              <Pagination
                defaultCurrent={dataList?.page || 1}
                pageSize={dataList?.limit || 10}
                current={dataList?.page}
                total={dataList.totalDocs}
                showSizeChanger={false}
                onChange={handleMessagesPagination}
              />
            </div>
          )}
          <MessagesList
            loading={isLoadingList}
            success={isSuccessList}
            firstLoad={firstLoad}
            list={firstLoad ? dataList?.docs : messagesArray}
            newMessage={currentMessage}
            reply={reply}
            scrollToMsg={scrollToMsg}
            setScrollToMsg={setScrollToMsg}
            onReply={onRelyHandle}
            setReply={setReply}
            ref={messagesListRef}
            currentPage={currentPage}
          />
          <CreateMessageMobile
            loading={sendMessageLoading}
            disabled={isLoadingList}
            reply={reply}
            setReply={setReply}
            onSend={onSendMessage}
            messagesListRef={messagesListRef}
            dataList={isSuccessList}
          />
        </ErrorBoundary>
      </div>
    )
  );
};

DiscussionMobile.defaultProps = {};
DiscussionMobile.propTypes = {
  discussionId: PropTypes.string,
};

export default DiscussionMobile;
